import { IConfigFunctionArgs } from 'Components/Worksheets/Core';
import { mergeConfig } from 'Components/Worksheets/Core/Helpers/configHelpers';
import getDerivedFromMeasurementsConfig from 'Components/Worksheets/MoistureContentWorksheet/derivedFromMeasurementsConfig';
import { FormatTypes } from 'Lib/Format';
import * as oPath from '../../../../../Lib/Object/Path/Immutable';
import qestIds from '../../../QestIds';
import getMeasuredUsingConfig from '../../measuredUsingConfig';
import getLabelConfig from './labelConfig';

const methods = ['A', 'B'];
const methodLabels = [
  { label: 'A - Nearest 1%', value: 'A' },
  { label: 'B - Nearest 0.1%', value: 'B' },
];

/**
 * Additional arguments to customise the returned config object
 * of an ASTM moisture content test
 */
export type AstmMoistureContentGetConfigFunctionArgs = {
  /**
   * Overrides the term "Material" in field labels
   */
  materialLabelOverride?: string;
  /**
   * Defines the valid values of the options for field `Preparation.Method`
   */
  validMethods?: typeof methods;
  /**
   * Defines a set of fields to set property `disabled` to true (by unprefixed field name)
   */
  disabledPaths?: string[];
};

const getConfig = (
  prefix: string,
  {
    materialLabelOverride = undefined,
    validMethods = methods,
    disabledPaths = [],
  }: AstmMoistureContentGetConfigFunctionArgs = {}
) =>
  mergeConfig(
    {
      [`${prefix}.Preparation.Method`]: {
        options: methods.map((e) => ({
          label: methodLabels.find((m) => m.value === e)?.label,
          value: e,
          disabled: !validMethods.includes(e),
        })),
      },
      [`${prefix}.Preparation.VisualNominalParticleSize`]: {
        disabled: disabledPaths.some((s) => s === 'Preparation.VisualNominalParticleSize'),
      },
      [`${prefix}.Preparation.ExcludedMaterial`]: {
        disabled: disabledPaths.some((s) => s === 'Preparation.ExcludedMaterial'),
      },
      [`${prefix}.Equipment.DryingTemperature`]: { uom: '°C', format: { nearest: 1 } },
      [`${prefix}.DryingTray.DryMassReadings.DryingTime`]: { uom: 'h', format: { nearest: 0.01 } },
      [`${prefix}.DryingTray.DateTimePlacedInOvenLocal`]: {
        format: { type: FormatTypes.Time, pattern: 'YYYY-MM-DD HH:mm' },
      },
      [`${prefix}.DryingTray.DryMassReadings.DateTimeRemovedFromOvenLocal`]: {
        format: { type: FormatTypes.Time, pattern: 'YYYY-MM-DD HH:mm' },
      },
      ...getMoistureContentPercent(`Results.MoistureContentPercent`, prefix, `Preparation.Method`),
    },
    getLabelConfig(prefix, materialLabelOverride),
    getMeasuredUsingConfig(prefix),
    getDerivedFromMeasurementsConfig(prefix),
    {
      [`${prefix}.Equipment.DryMassBalance`]: { defaultReadability: 0.1, defaultUom: 'g' },
      [`${prefix}.Equipment.Balance`]: { defaultReadability: 0.1, defaultUom: 'g' },
    }
  );

const getMoistureContentPercent = (moistureContentPercentPath: string, prefix: string, methodPath: string) => ({
  [`${prefix}.${moistureContentPercentPath}`]: (configArgs: IConfigFunctionArgs) => {
    let method = oPath.get(configArgs.worksheet, `${prefix}.${methodPath}`);

    //If the method cannot be found via the MethodPath then chances are this is an embedded MoistureContent which is
    //part of a trial/specimen. In order to retrieve the method we need to know which trial/specimen to look at.
    //As such we use the name/path of the property in question which contains the necessary indexes and then look
    //for the preparation method.
    if (!method && configArgs.name.endsWith(moistureContentPercentPath))
      method = oPath.get(
        configArgs.worksheet,
        `${configArgs.name.slice(0, -moistureContentPercentPath.length)}${methodPath}`
      );
    return { format: { nearest: method === 'A' ? 1 : 0.1 } };
  },
});

export { getConfig, getMoistureContentPercent };

export default {
  [qestIds.moistureContent.ASTM_D_2216.rev2019]: getConfig,
};
